// @flow

// A pin can be displayed with carousel ui because (1) it has truthy carousel_data in pin object, or
// (2) it's a product pin v2 with multiple product images in pin.product_pin_data.items[0].additional_images.

// To display these 2 carousel pins using same carousel ui components, we map additional_images
// in product_pin_data to a new carousel_data object, to match the structure of pin.carousel_data.

// This reconstruction goes through 3 main steps:
// 1. put pin image as the first image in carousel_slots
// 2. put images in pin.product_pin_data.items[0].additional_images into carousel_slots, following the pin image
// 3. unify the image sizes in carousel_slots, to make sure that for one image key (e.g. '236x'), images
// in every slot have same width and height. Otherwise, there would be carousel pinrep display issues.

import type { CarouselSlotData, ProductPinItem } from 'app/packages/pinterest-flow-types/pin';

// Use Object beacasue `images` coming from PinRep and Closeup have different types
type Images = {
  [size: string]: Object,
};

// imageKeySize object is used to save unified image height and width for an imageKey.
type imageKeySize = {|
  imageKey: '236x' | '474x' | '564x' | '736x',
  height: number,
  width: number,
|};

// Image keys that may be used for carousel pinrep.
const IMAGE_KEYS = ['236x', '474x', '564x', '736x'];

// Sometimes we have different image sizes with a same image key in different slots. Here we unify
// them to min height or width, so images will get cropped to a same size for carousel display.
function unifySlotImageSizes(carouselSlots: Array<CarouselSlotData>) {
  // Unify image sizes in slots for a given image key.
  function unifyImageSizes(imageKey) {
    // Get min height or width for images in different slot with a same image key.
    const minHeight = Math.min(
      ...carouselSlots.map(
        (slotItem) => (slotItem.images[imageKey] && slotItem.images[imageKey].height) || Infinity,
      ),
    );
    const minWidth = Math.min(
      ...carouselSlots.map(
        (slotItem) => (slotItem.images[imageKey] && slotItem.images[imageKey].width) || Infinity,
      ),
    );
    return { imageKey, height: minHeight, width: minWidth };
  }

  function overrideSlotImageSize(
    slot: CarouselSlotData,
    { imageKey, height, width }: imageKeySize,
  ) {
    if (slot.images[imageKey]) {
      slot = {
        ...slot,
        images: { ...slot.images, [imageKey]: { ...slot.images[imageKey], height, width } },
      };
    }
    return slot;
  }

  // Get unified image sizes for each image key.
  const unifiedImageKeySizes = IMAGE_KEYS.map(unifyImageSizes);

  // For each image key, override height and width of images in each slot with minHeight and minWidth.
  return carouselSlots.map<CarouselSlotData>((slot) =>
    unifiedImageKeySizes.reduce(overrideSlotImageSize, slot),
  );
}

function mapAdditionalImageToCarouselSlot(
  carouselSlots: Array<CarouselSlotData>,
  additionalImage: Images,
) {
  carouselSlots.push({
    images: additionalImage && additionalImage.canonical_images,
  });
  return carouselSlots;
}

export default function mapProductImagesToCarouselData(
  images: Images,
  item: ProductPinItem,
  index: ?number,
) {
  // Put pin image into the first slot of carousel.
  const pinImageSlot = [{ images }];
  // Get product additional images and put them into carousel slots, following the pin image.
  const additionalImages = item && item.additional_images;
  const carouselSlots = additionalImages.reduce(mapAdditionalImageToCarouselSlot, pinImageSlot);

  // Unify image sizes in slots and return the object of same structure as carousel_data in normal carousel pin.
  return { carousel_slots: unifySlotImageSizes(carouselSlots), index: index || 0 };
}
